<template>
  <div class="report">
    <h4 v-if="data.title">{{ data.title }}</h4>
    <div v-for="(sec, i) in sections" :key="i" class="sec">
      <div v-if="sec.label" class="lbl">{{ sec.label }}</div>

      <!-- 键值：总览 / 趋势等 -->
      <div v-if="sec.type === 'kv'" class="kv">
        <span v-for="(it, j) in sec.items || []" :key="j" class="i">
          {{ it.k }}
          <b>{{ it.v }}</b>
        </span>
      </div>

      <!-- 分布：条形 + 图例（前端自动算百分比与配色） -->
      <template v-else-if="sec.type === 'bar'">
        <div class="bar">
          <i
            v-for="(it, j) in sec.items || []"
            :key="j"
            :style="{ width: barPct(sec.items, it) + '%', background: barColor(j, it.name) }"
          ></i>
        </div>
        <div class="legend">
          <span v-for="(it, j) in sec.items || []" :key="j">
            <s :style="{ background: barColor(j, it.name) }"></s>
            {{ it.name }} {{ it.count }}
          </span>
        </div>
      </template>

      <!-- 缺陷号等可点 chip -->
      <div v-else-if="sec.type === 'chips'" class="chips">
        <a v-for="(it, j) in sec.items || []" :key="j" class="chip" :href="it.link || '#'">{{ it.text }}</a>
      </div>

      <!-- 备注 -->
      <div v-else-if="sec.type === 'note'" class="note">{{ sec.text }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface ReportSection {
  label?: string;
  type: 'kv' | 'bar' | 'chips' | 'note';
  items?: any[];
  text?: string;
}
interface ReportData {
  title?: string;
  sections?: ReportSection[];
}

const props = defineProps<{ data: ReportData }>();

const sections = computed(() => props.data?.sections ?? []);

// 严重度用语义色，其余按调色板轮转
const SEV: Record<string, string> = {
  致命: '#dc2626',
  严重: '#f97316',
  一般: '#eab308',
  提示: '#3b82f6',
  建议: '#94a3b8',
};
const PALETTE = ['#1677ff', '#52a0ff', '#16a34a', '#eab308', '#f97316', '#dc2626', '#8b5cf6', '#94a3b8'];
const barColor = (index: number, name?: string) => (name && SEV[name]) || PALETTE[index % PALETTE.length];

const barPct = (items: any[], it: any) => {
  const total = (items || []).reduce((s, x) => s + (Number(x.count) || 0), 0);
  return total > 0 ? Math.round(((Number(it.count) || 0) / total) * 1000) / 10 : 0;
};
</script>

<style scoped>
.report {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 12px;
  padding: 12px 13px;
  font-size: 13px;
  width: 100%;
}
.report h4 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}
.sec {
  margin-top: 12px;
}
.sec:first-of-type {
  margin-top: 4px;
}
.lbl {
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa3af;
  font-weight: 600;
  margin-bottom: 5px;
}
.kv {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-variant-numeric: tabular-nums;
}
.kv .i {
  font-size: 12.5px;
  color: #6b7280;
}
.kv .i b {
  color: #1f2937;
  font-weight: 700;
}
.bar {
  display: flex;
  height: 8px;
  border-radius: 5px;
  overflow: hidden;
  margin: 6px 0 8px;
  background: #f0f2f5;
}
.bar i {
  display: block;
}
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 11.5px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}
.legend s {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  display: inline-block;
  margin-right: 4px;
  text-decoration: none;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.chip {
  font-family: ui-monospace, sfmono-regular, menlo, consolas, monospace;
  font-size: 12px;
  color: #1677ff;
  background: #e8f1ff;
  padding: 2px 8px;
  border-radius: 6px;
  text-decoration: none;
  border: 1px solid #d6e6ff;
}
.chip:hover {
  background: #dcebff;
}
.note {
  margin-top: 10px;
  font-size: 11.5px;
  color: #9aa3af;
  border-top: 1px dashed #eef0f3;
  padding-top: 8px;
}
</style>
